import React, { useEffect, useRef, useState } from "react";
import Loader from "../../../common/loader";
import BankerCardView from "./bankersCardView";
import BankerTableView from "./bankersTableView";
import AddBankerModal from "./addBanker";
import InviteBankerDialog from "./inviteBanker";
import {
  IconButton,
  Button,
  Paper,
  InputBase,
  Snackbar,
  Alert,
  Pagination,
  Typography,
  Box,
} from "@mui/material";
import {
  Search as SearchIcon,
  PersonAddAlt as PersonAddAltIcon,
  Email as EmailIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  getBankers,
  deleteBankerById,
  blockBanker,
  importBankers,
} from "../../../services/banker.service";
import { Business } from "../../../validation/business.validation";
import { Banker } from "../../../validation/banker.validations";
import { getLoginUserData } from "../../../services/account.service";

const defaultBusiness: Business = {
  name: "",
  ein: "",
  email: "",
  phone: "",
  address: "",
  synonyms: "",
  website: "",
  id: "",
};

const Bankers: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<"grid" | "table">("grid");
  const [bankers, setBankers] = useState([]);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    search: "",
    businessId: "",
  });
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [openModal, setOpenModal] = useState(false);
  const [user, setUser] = React.useState<Business>(defaultBusiness);
  const [editBankerDetails, setEditBankerDetails] =
    React.useState<Banker | null>(null);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });

  const totalPages = Math.ceil(paginationData.total / paginationData.limit);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const user = await getLoginUserData();
        if (user?.id) {
          setUser(user);
          const updatedQuery = {
            ...query,
            businessId: user?.businessId ? user?.businessId : user.id,
          };
          setQuery(updatedQuery);
          await fetchBankers(updatedQuery);
        }
      } catch (error: any) {
        showSnackbar("error", error?.message || "Failed to load co-owners.");
      } finally {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.businessId) fetchBankers(query);
    }, 700);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search]);

  const fetchBankers = async (payload: any) => {
    setIsLoading(true);
    try {
      const res = await getBankers(payload);
      setBankers(res.data);
      setPaginationData({ page: res.page, limit: res.limit, total: res.total });
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to fetch bankers.");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBanker = async (banker: any) => {
    setIsLoading(true);
    try {
      await deleteBankerById(banker.id);
      showSnackbar("success", "Banker deleted successfully.");
      fetchBankers(query);
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to delete banker.");
    } finally {
      setIsLoading(false);
    }
  };

  const deactivateBanker = async (banker: any) => {
    setIsLoading(true);
    try {
      await blockBanker(banker.id, { isActive: !banker.isActive });
      showSnackbar(
        "success",
        `Banker ${banker.isActive ? "deactivated" : "activated"} successfully.`
      );
      fetchBankers(query);
    } catch (error: any) {
      showSnackbar(
        "error",
        error?.message || "Failed to update banker status."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const editBanker = async (baker: Banker) => {
    if (baker) {
      setOpenModal(true);
      setEditBankerDetails(baker);
    }
  };

  const showSnackbar = (
    severity: "success" | "error" | "info",
    message: string
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery((prev) => ({ ...prev, search: event.target.value }));
  };

  const handlePageChange = (_: any, value: number) => {
    const updatedQuery = { ...query, page: value };
    setQuery(updatedQuery);
    fetchBankers(updatedQuery);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    const isExcelFile = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!isExcelFile) {
      showSnackbar("error", "Please select an Excel or CSV file.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await importBankers(file, query.businessId);
      const message = `Imported ${result.createdCount} banker${
        result.createdCount === 1 ? "" : "s"
      }.`;
      showSnackbar("success", message);
      fetchBankers({ ...query, page: 1 });
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to import bankers.");
    } finally {
      setIsLoading(false);
    }
  };

  // put this before the return
  let content;

  if (bankers.length === 0) {
    content = (
      <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4">
        <div className="flex items-center justify-center mb-4">
          <div className="animate-pulse">
            <SearchRoundedIcon
              className="text-gray-400"
              sx={{ fontSize: 48 }}
            />
          </div>
        </div>
        <Typography variant="h6" className="mt-4 text-gray-700">
          No Bankers Found
        </Typography>
        <Typography variant="body2" className="text-gray-500">
          You can try adjusting your search or add a new banker to get started.
        </Typography>
      </Box>
    );
  } else if (view === "grid") {
    content = (
      <BankerCardView
        bankers={bankers}
        onEdit={editBanker}
        onDelete={deleteBanker}
        onBlock={deactivateBanker}
        user={user}
      />
    );
  } else {
    content = (
      <BankerTableView
        bankers={bankers}
        onEdit={editBanker}
        onDelete={deleteBanker}
        onBlock={deactivateBanker}
        user={user}
      />
    );
  }

  return (
    <div>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 py-2 px-4">
          <Paper
            component="form"
            className="flex items-center border border-gray-300 rounded-full px-2"
            elevation={0}
            sx={{
              borderRadius: 2,
              py: 0.5,
              fontWeight: 600,
              minWidth: { xs: "200px", sm: "300px", md: "350px" },
              maxWidth: { xs: "100%", sm: "400px", md: "450px" },
              width: "100%",
            }}
          >
            <SearchIcon className="text-gray-400 ml-2" />
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search banker"
              value={query.search}
              onChange={handleSearchChange}
            />
          </Paper>
          <div className="flex items-center flex-wrap gap-3">
            <Button
              variant="contained"
              startIcon={<PersonAddAltIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
              borderRadius: 2,
              py: 0.5,
              fontWeight: 600,
              fontSize: "1rem",
            }}
            >
              Add Banker
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleImportFileChange}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleImportClick}
              disabled={!query.businessId}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Import Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setOpenInviteDialog(true)}
               sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Invite Banker
            </Button>
            <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden">
              <IconButton
                onClick={() => setView("grid")}
                className={`!p-2 ${view === "grid" ? "bg-gray-100" : ""}`}
              >
                <ViewModuleIcon
                  className={view === "grid" ? "text-black" : "text-gray-500"}
                />
              </IconButton>
              <IconButton
                onClick={() => setView("table")}
                className={`!p-2 ${view === "table" ? "bg-gray-100" : ""}`}
              >
                <ViewListIcon
                  className={view === "table" ? "text-black" : "text-gray-500"}
                />
              </IconButton>
            </div>
          </div>
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {content}
            {bankers.length > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm gap-2">
                <Pagination
                  count={totalPages}
                  page={paginationData.page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="medium"
                />
                <div className="text-sm text-gray-600 text-center sm:text-right">
                  Showing {(paginationData.page - 1) * paginationData.limit + 1}{" "}
                  to{" "}
                  {Math.min(
                    paginationData.page * paginationData.limit,
                    paginationData.total
                  )}{" "}
                  of {paginationData.total} results
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <AddBankerModal
        open={openModal}
        handleClose={() => {
          setOpenModal(false);
          setEditBankerDetails(null);
          fetchBankers(query);
        }}
        businessId={query.businessId}
        editMode={!!editBankerDetails}
        bankerData={editBankerDetails || undefined}
      />
      <InviteBankerDialog
        open={openInviteDialog}
        onClose={() => setOpenInviteDialog(false)}
        user={user}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Bankers;
